import { extractIdFromIri } from "./extractFromIri";
import { getTextByLang } from "./formUtils";
import { getBackendLangCode } from "./constants";
import { normalizeVocabularyText } from "./vocabulary";

export function getParticipantDisplayName(participant, lang) {
  return (
    getTextByLang(participant?.preferredLabel, lang) ||
    getTextByLang(participant?.identifier?.[0]?.schemeName, lang) ||
    participant?.identifier?.[0]?.notation ||
    ""
  );
}

export function getParticipantVocabularyLabel(value, options = [], lang = "en") {
  if (value === null || value === undefined || value === "") return "";

  const normalizedInput = normalizeVocabularyText(value);
  const backendLang = getBackendLangCode(lang);
  const items = Array.isArray(options) ? options : [];

  const match = items.find((item) =>
    [
      item?.concept_iri,
      item?.code,
      item?.label,
      ...(item?.langName && typeof item.langName === "object"
        ? Object.values(item.langName)
        : []),
    ]
      .filter(Boolean)
      .map(normalizeVocabularyText)
      .includes(normalizedInput),
  );

  return (
    match?.langName?.[backendLang] ||
    match?.label ||
    match?.langName?.eng ||
    Object.values(match?.langName || {}).find(Boolean) ||
    extractIdFromIri(value) ||
    value ||
    ""
  );
}

export function mapParticipantVocabularyValues(values, options = [], lang = "en") {
  if (Array.isArray(values)) {
    return values
      .map((item) => getParticipantVocabularyLabel(item, options, lang))
      .filter(Boolean);
  }

  if (values) {
    return [getParticipantVocabularyLabel(values, options, lang)].filter(Boolean);
  }

  return [];
}

export function formatParticipantVocabularyValues(
  values,
  options = [],
  lang = "en",
  separator = ", ",
  empty = "",
) {
  const mapped = mapParticipantVocabularyValues(values, options, lang);
  return mapped.length > 0 ? mapped.join(separator) : empty;
}

export function getParticipantLocation(
  participant,
  countryOptions = [],
  lang = "en",
  provinceOptions = [],
) {
  const address = participant?.address || {};
  const country =
    getParticipantVocabularyLabel(address.adminUnit1, countryOptions, lang) || "";
  const province =
    getParticipantVocabularyLabel(address.adminUnit2, provinceOptions, lang) || "";
  return [province, country].filter(Boolean).join(", ");
}

export function getParticipantStatusLabel(status, t) {
  const normalizedStatus = String(status || "UNKNOWN").toUpperCase();
  const key = `participant.statusValues.${normalizedStatus}`;
  const resolved = t(key);
  return resolved === key ? normalizedStatus : resolved;
}

export function getParticipantTechnicalStatusLabel(status, t) {
  const normalizedStatus = String(status || "UNKNOWN").toUpperCase();
  const key = `participant.technicalStatusValues.${normalizedStatus}`;
  const resolved = t(key);
  return resolved === key ? normalizedStatus : resolved;
}

export function getParticipantUpdatedDate(participant, locale = "en") {
  const raw = participant?.updated_at || participant?.created_at || "";
  if (!raw) return "—";

  return new Date(raw).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function getParticipantPurposePreview(participant, limit = 2) {
  if (Array.isArray(participant?.purpose)) return participant.purpose.slice(0, limit);
  if (participant?.purpose) return [participant.purpose];
  return [];
}

export function buildParticipantSearchText(participant, lang) {
  if (!participant || typeof participant !== "object") return "";

  const description = getTextByLang(participant.description, lang) || "";
  const classification = Array.isArray(participant.classification)
    ? participant.classification.join(" ")
    : participant.classification || "";
  const purpose = Array.isArray(participant.purpose)
    ? participant.purpose.join(" ")
    : participant.purpose || "";

  return [
    getParticipantDisplayName(participant, lang),
    description,
    classification,
    purpose,
    participant.address?.adminUnit1,
    participant.address?.adminUnit2,
    participant.address?.postName,
    participant.address?.postCode,
    participant.identifier?.[0]?.notation,
  ]
    .filter(Boolean)
    .join(" ");
}

export function getPostNameFromPostalCodeLabel(selectedValue, options = []) {
  const selectedOption = options.find((option) => option.value === selectedValue);
  if (!selectedOption?.label) return "";

  const [, ...labelParts] = String(selectedOption.label).split("-");
  return labelParts.join("-").trim();
}
